import { useState } from "react";
import BackIcon from "../../assets/icons/back.svg";
import { FormContainer } from "../../pages/tmrw-admin/ClinicDetails.styles";
import { Grid } from "../../pages/tmrw-admin/Grading.styles";
import Button from "../button/Button";
import InputBox from "../input/InputBox";
import Textarea from "../input/Textarea";
import {
  HeaderContainer,
  LeftSection,
  Heading,
  Icon,
} from "./AddRating.styles";
import validator from "../../validarions/addRating.validation";
import ChipInput from "../input/ChipInput";
import ChipDropdown from "../input/ChipDropdown";
import {
  toolTipMessagesOocyte,
  toolTipMessagesEmbroy,
} from "../../utils/messages";
import { useToast } from "../../contexts/ToastContext";
import {
  addEmbroysRating,
  addOocyteRating,
  deleteOneGrade,
} from "../../services/clinic-detail.service";
import { AddRatingProps } from "../../types";
import ActionModal from "../gradingmodal/ActionModal";

const formItems = [
  {
    id: "Rating",
    labelName: "Rating",
    placeHolderName: "Rating, e.g. “Excellent”",
    type: "text",
  },
];

type formField = {
  value: string;
  error: string;
};

function AddRating({ goBack, active, currentlySelected }: AddRatingProps) {
  const { description, grades, rating } = currentlySelected || {};
  const [formFields, setFormFields] = useState<{
    Rating: formField;
    Grade: formField;
    Description: formField;
  }>({
    Rating: {
      value: rating ? rating : "",
      error: "",
    },
    Grade: {
      value: "",
      error: "",
    },
    Description: {
      value: description ? description : "",
      error: "",
    },
  });

  const [gradeList, setGradeList] = useState<string[]>(grades ? grades : []);

  const [openModal, setOpenModal] = useState(false);
  const [isDelete, setIsDelete] = useState(false);

  const toast = useToast();

  const changeHandler = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const errorMessage =
      name === "Grade"
        ? validator[name as keyof typeof validator](value, gradeList)
        : validator[name as keyof typeof validator](value, gradeList);
    setFormFields((prev) => ({
      ...prev,
      [name]: {
        value,
        error: errorMessage,
      },
    }));
  };

  const onStrokeEnterKey = () => {
    const value = formFields["Grade"].value;

    if (gradeList.includes(value)) {
      setFormFields((prev) => ({
        ...prev,
        Grade: {
          value,
          error: "Grade has already been added to another rating",
        },
      }));
      return;
    }
    if (value) {
      setGradeList((prev) => [...prev, value]);
      setFormFields((prev) => ({
        ...prev,
        Grade: {
          value: "",
          error: "",
        },
      }));
    }
  };

  const onCloseChip = (value: string) => {
    const updateList = gradeList.filter((grade) => !(grade === value));
    setGradeList(updateList);
  };

  const resetFormDataError = (keyName: string) => {
    setFormFields((prev) => ({
      ...prev,
      [keyName]: {
        ...formFields[keyName as keyof typeof formFields],
        error: "",
      },
    }));
  };

  const renderGradeInput = () => {
    return active === "Oocytes" ? (
      <ChipDropdown
        id="Grade"
        labelName="Associated Grade(s)"
        placeHolderName="Maturity Levels, e.g. “MI” and “MII”"
        error={formFields["Grade"].error}
        style={{ width: "48%" }}
        showTooltip={true}
        chips={gradeList}
        setGradeList={setGradeList}
        onCloseChip={onCloseChip}
        toolTipMessage={toolTipMessagesOocyte["Grade"]}
        resetFormDataError={resetFormDataError}
      />
    ) : (
      <ChipInput
        id="Grade"
        labelName="Associated Grade(s)"
        placeHolderName="Grades, e.g. “3AA” or “Abnormal”"
        value={formFields["Grade"].value}
        handler={changeHandler}
        error={formFields["Grade"].error}
        type="text"
        style={{ width: "48%" }}
        showTooltip={true}
        chips={gradeList}
        onStrokeEnterKey={onStrokeEnterKey}
        onCloseChip={onCloseChip}
        toolTipMessage={toolTipMessagesEmbroy["Grade"]}
      />
    );
  };

  const getRatingTooltip = () => {
    if (active === "Oocytes") return toolTipMessagesOocyte["Rating"];
    else return toolTipMessagesEmbroy["Rating"];
  };

  const getUrl = (
    action: "embryo" | "oocyte",
    type: "create" | "update" | "delete"
  ) => {
    ///v1/clinic/grading/update/embryo/6
    const { id } = currentlySelected || {};
    return id
      ? `/v1/clinic/grading/${type}/${action}/${id}`
      : `/v1/clinic/grading/${type}/${action}/`;
  };

  const onCloseModal = () => {
    setOpenModal(false);
    setIsDelete(false);
  };

  const onOpenModal = () => {
    setOpenModal(true);
  };

  const getActionType = () => {
    const { id } = currentlySelected || {};
    if (isDelete && id) return "delete";
    else if (id) return "update";
    else return "create";
  };

  const handleAction = () => {
    const payload = {
      rating: formFields["Rating"].value,
      grades: gradeList,
      description: formFields["Description"].value,
    };
    active === "Oocytes"
      ? addOocyteRating(
          payload,
          toast?.displayMessage,
          goBack,
          getUrl("oocyte", getActionType())
        )
      : addEmbroysRating(
          payload,
          toast?.displayMessage,
          goBack,
          getUrl("embryo", getActionType())
        );
    onCloseModal();
  };

  const handleDeleteRating = () => {
    const action = active === "Oocytes" ? "oocyte" : "embryo";
    deleteOneGrade(
      getUrl(action, getActionType()),
      goBack,
      toast?.displayMessage
    );
    onCloseModal();
  };

  const handleSubmit = () => {
    let hasError = false;
    for (let key in formFields) {
      if (formFields[key as keyof typeof formFields].error) {
        hasError = true;
        break;
      }
    }

    for (let key in formFields) {
      const value = formFields[key as keyof typeof formFields].value;
      const errorMessage = validator[key as keyof typeof validator](
        value,
        gradeList
      );
      if (errorMessage) {
        hasError = true;
        setFormFields((prev) => ({
          ...prev,
          [key]: {
            value,
            error: errorMessage,
          },
        }));
      }
    }

    if (!hasError) {
      onOpenModal();
    }
  };

  const modalProps = {
    onCloseModal,
    text: isDelete
      ? "Are you sure you want to delete?"
      : "Would you like to save changes?",
    firstBtnText: isDelete ? "Delete" : "Save Changes",
    secondBtnText: isDelete ? "Cancel" : "No, go back",
    actionHandler: isDelete ? handleDeleteRating : handleAction,
  };

  return (
    <div>
      <HeaderContainer>
        <LeftSection>
          <Icon onClick={goBack} src={BackIcon} alt="back-icon" />
          <Heading>{currentlySelected ? "Edit Rating" : "Add Rating"}</Heading>
        </LeftSection>
        <div>
          {currentlySelected && (
            <Button
              text="Delete"
              onClick={() => {
                setIsDelete(true);
                onOpenModal();
              }}
              disabled={false}
              style={{
                width: "90px",
                height: "36px",
                padding: "10px 20px",
                fontSize: "14.5px",
                background: "rgba(202, 43, 29, 0.1)",
                color: "#CA2B1D",
                marginRight: "8px",
              }}
            />
          )}
          <Button
            text="Save Changes"
            onClick={handleSubmit}
            disabled={false}
            style={{
              width: "150px",
              height: "36px",
              padding: "10px 20px",
              fontSize: "14.5px",
            }}
          />
        </div>
      </HeaderContainer>
      <div>
        <FormContainer>
          {formItems.map((formItem, idx) => (
            <Grid key={idx}>
              <InputBox
                value={
                  formFields[formItems[idx].id as keyof typeof formFields].value
                }
                handler={changeHandler}
                {...formItem}
                style={{ width: "95%" }}
                showTooltip={true}
                error={
                  formFields[formItems[idx].id as keyof typeof formFields].error
                }
                toolTipMessage={getRatingTooltip()}
              />
            </Grid>
          ))}
          {renderGradeInput()}
          <Textarea
            id="Description"
            labelName="Rating Description (Optional)"
            placeHolderName="Description"
            value={formFields["Description"].value}
            handler={changeHandler}
            error={formFields["Description"].error}
          />
        </FormContainer>
      </div>
      {openModal && (
        <ActionModal {...modalProps} isDelete={Boolean(isDelete)} />
      )}
    </div>
  );
}

export default AddRating;
